import { useEffect, useRef, useState } from "react";
import * as devicesApi from "../../api/devices";
import { Device } from "../../api/devices";
import { modelloDaDevice } from "../../utils/modelloDaDevice";
import { Input } from "./Input";

/**
 * Che cosa è il pezzo, in una riga. Prima la tendina mostrava solo
 * `modello_raw`, che è valorizzato SOLO quando la riconciliazione col catalogo
 * NON è riuscita: i device agganciati a un modello — cioè quelli che
 * precompilano di più — comparivano senza descrizione, come se non si sapesse
 * niente di loro.
 */
function descrizione(d: Device): string {
  const { marca, modello } = modelloDaDevice(d);
  return [marca, modello].filter(Boolean).join(" ");
}

interface DeviceFisicoAutocompleteProps {
  label: string;
  /** Matricola scritta o scelta. È il testo che l'utente vede nel campo. */
  value: string;
  onChange: (value: string) => void;
  /** Device attualmente collegato ("" = nessuno). */
  deviceId: string;
  onDeviceIdChange: (deviceId: string) => void;
  /** Chiamato quando l'utente sceglie un pezzo dalla tendina — il chiamante lo
      usa per precompilare i campi che descrivono lo stesso oggetto (marca,
      modello). Non viene chiamato digitando: vedi il commento sotto. */
  onSelect?: (device: Device) => void;
  /**
   * Sede presso cui si svolge il lavoro. Quando c'è, i device che stanno lì
   * compaiono in cima senza bisogno di digitare niente.
   */
  sedeId?: string | null;
  hint?: string;
  placeholder?: string;
}

/**
 * Sceglie il DEVICE FISICO — il pezzo con la sua matricola — proponendo per
 * primi quelli che stanno nella sede del record.
 *
 * ## Perché per sede e non cercando la matricola nel testo
 *
 * L'alternativa era riconoscere la matricola dentro il titolo o le note. Non
 * regge: tre matricole del parco sono lunghe cinque caratteri (`00002`,
 * `DO002`, `DO007`) e cercarle in testo libero aggancerebbe qualunque riga che
 * contenga quella sequenza — un numero d'ordine, una data, un importo. Un
 * aggancio sbagliato è peggio di uno mancante, perché sembra un dato.
 *
 * La sede invece è una lettura su indice: `devices.sede_id` è una posizione
 * dichiarata, e ogni sede posizionata ha esattamente un device. La proposta è
 * quindi una, e non è un indovinello.
 *
 * ## Suggerimento, non vincolo
 *
 * La ricerca libera su tutto il parco resta sempre disponibile sotto. Una
 * cassaforte può essere stata spostata senza che nessuno abbia aggiornato la
 * posizione, e un campo che rifiuta la matricola giusta è peggio di uno
 * permissivo. Per lo stesso motivo il testo digitato non viene cancellato
 * quando non corrisponde a nulla: su un'attivazione è un valore legittimo di
 * suo, e può riferirsi a un pezzo non ancora censito.
 *
 * ## La chiave la gestisce il componente
 *
 * Come in `SedeAutocomplete`, e per la stessa ragione: chi sceglie dalla
 * tendina e poi modifica il testo non deve tenersi la chiave vecchia, che
 * collegherebbe il record a un device diverso da quello scritto. La regola sta
 * qui, in un posto solo.
 */
export function DeviceFisicoAutocomplete({
  label,
  value,
  onChange,
  deviceId,
  onDeviceIdChange,
  onSelect,
  sedeId,
  hint,
  placeholder,
}: DeviceFisicoAutocompleteProps) {
  const [inSede, setInSede] = useState<Device[]>([]);
  const [trovati, setTrovati] = useState<Device[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // I device della sede si caricano una volta sola quando la sede cambia: sono
  // pochissimi (una sede posizionata ne ha uno) e servono anche a campo vuoto,
  // che è il momento in cui il suggerimento vale di più.
  useEffect(() => {
    if (!sedeId) {
      setInSede([]);
      return;
    }
    devicesApi
      .listDevices({ sede_id: sedeId })
      // Senza matricola un pezzo non si può collegare da qui: il record lo
      // aggancia dal testo della matricola, e un campo vuoto non aggancia nulla.
      .then(({ records }) => setInSede(records.filter((d) => d.matricola !== null)))
      .catch(() => setInSede([])); // il suggerimento è un extra: se manca, il campo resta libero
  }, [sedeId]);

  // La ricerca libera interroga il backend, come ClienteAutocomplete: le
  // matricole sono centinaia e cresceranno, quindi non si precarica il parco.
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setTrovati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      devicesApi
        .listDevices({ q })
        .then(({ records }) => {
          if (!annullato) setTrovati(records);
        })
        .catch(() => {
          if (!annullato) setTrovati([]);
        });
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function scegli(d: Device) {
    onChange(d.matricola ?? "");
    onDeviceIdChange(d.id);
    onSelect?.(d);
    setOpen(false);
  }

  // I device della sede non si ripetono più in basso se la ricerca li ha già
  // restituiti: due volte la stessa riga fa sembrare che siano due pezzi.
  const idsInSede = new Set(inSede.map((d) => d.id));
  const altri = trovati.filter((d) => !idsInSede.has(d.id));
  const mostraTendina = open && (inSede.length > 0 || altri.length > 0);

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      <Input
        label={label}
        value={value}
        placeholder={placeholder ?? "Matricola della cassaforte"}
        hint={hint}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          // Modificando il testo la scelta precedente decade: vedi il commento
          // sulla chiave, sopra.
          if (deviceId) onDeviceIdChange("");
          setOpen(true);
        }}
      />

      {mostraTendina && (
        <ul className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-white shadow-card">
          {inSede.length > 0 && (
            <>
              <li className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink/40">
                In questa sede
              </li>
              {inSede.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-ink hover:bg-ink/[0.04]"
                    onClick={() => scegli(d)}
                  >
                    <span className="font-medium">{d.matricola}</span>
                    {descrizione(d) && <span className="text-ink/45"> — {descrizione(d)}</span>}
                  </button>
                </li>
              ))}
            </>
          )}

          {altri.length > 0 && (
            <>
              <li className="border-t border-line px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink/40">
                Altrove nel parco
              </li>
              {altri.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-ink hover:bg-ink/[0.04]"
                    onClick={() => scegli(d)}
                  >
                    <span className="font-medium">{d.matricola}</span>
                    {descrizione(d) && <span className="text-ink/45"> — {descrizione(d)}</span>}
                  </button>
                </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
